import { Card, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBrain,
  faClipboardCheck,
  faEye,
  faUser,
  faUsers,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import Button from "app/storybookComponents/Button";
import LoginHeader from "app/containers/Login/LoginHeader";
import { McpFlowAction } from "./mcpAuthorize.api";
import "./McpConsentCard.scss";

interface AccessItem {
  icon: IconDefinition;
  label: string;
  note?: string;
}

const ACCESS_ITEMS: AccessItem[] = [
  { icon: faUser, label: "Your profile" },
  { icon: faBrain, label: "Your coaching history" },
  { icon: faClipboardCheck, label: "Your check-ins, teams and goals" },
  {
    icon: faUsers,
    label: "Colleague, team and check-in details",
    note: "Only where your organization's visibility settings allow.",
  },
];

interface Props {
  clientName: string;
  redirectHost: string;
  /** The action currently being submitted, or null when idle. */
  submittingAction: McpFlowAction | null;
  error: string | null;
  onAllow: () => void;
  onDeny: () => void;
}

function ButtonLabel({
  busy,
  idle,
  busyLabel,
}: {
  busy: boolean;
  idle: string;
  busyLabel: string;
}) {
  if (!busy) return <>{idle}</>;
  return (
    <>
      <Spinner animation="border" size="sm" aria-hidden="true" />
      <span>{busyLabel}</span>
    </>
  );
}

export default function McpConsentCard({
  clientName,
  redirectHost,
  submittingAction,
  error,
  onAllow,
  onDeny,
}: Props) {
  const submitting = submittingAction !== null;

  return (
    <div className="login-page">
      <Card className="mcp-consent" aria-busy={submitting}>
        <LoginHeader title={`Authorize ${clientName}`} />
        <p className="mcp-consent__intro">
          <strong>{clientName}</strong> is asking to view your Coach Bo data in
          Criteria. It will be able to see:
        </p>
        <ul className="mcp-consent__access">
          {ACCESS_ITEMS.map(({ icon, label, note }) => (
            <li key={label} className="mcp-consent__access-item">
              <span className="mcp-consent__access-icon" aria-hidden="true">
                <FontAwesomeIcon icon={icon} />
              </span>
              <span>
                <span className="mcp-consent__access-label">{label}</span>
                {note ? (
                  <span className="mcp-consent__access-note">{note}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
        <div className="mcp-consent__readonly" role="note">
          <FontAwesomeIcon icon={faEye} aria-hidden="true" />
          <div>
            <strong>Read-only access</strong>
            <p>
              It can't create, modify, or delete anything, and it never receives
              admin access.
            </p>
          </div>
        </div>
        <p className="mcp-consent__redirect">
          After you choose, you'll be sent back to{" "}
          <strong>{redirectHost}</strong>.
        </p>
        {error ? (
          <p className="mcp-consent__error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="mcp-consent__actions">
          <Button
            variant="primary"
            className="mcp-consent__button"
            disabled={submitting}
            onClick={onAllow}
            dataTestId="mcp-allow"
          >
            <ButtonLabel
              busy={submittingAction === "allow"}
              idle="Allow"
              busyLabel="Allowing…"
            />
          </Button>
          <Button
            variant="secondary-blue"
            className="mcp-consent__button"
            disabled={submitting}
            onClick={onDeny}
            dataTestId="mcp-deny"
          >
            <ButtonLabel
              busy={submittingAction === "deny"}
              idle="Deny"
              busyLabel="Denying…"
            />
          </Button>
        </div>
      </Card>
    </div>
  );
}
